// Accordions.jsx
import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from "react-headless-accordion";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FiSearch, FiX } from "react-icons/fi";

/* ---------------------------------- */
/*               CONFIG               */
/* ---------------------------------- */
const LANG = {
  HE: "he",
  EN: "en",
};

const BOOKING_URL = "https://schedule.easybizy.net/devorahyaffebarr/welcome";

// Umami analytics — no-op when the script is blocked or not loaded
function track(name, data) {
  if (window.umami) window.umami.track(name, data);
}

const MERGED_LASER_CATEGORY = {
  [LANG.HE]: "טיפולי לייזר",
  [LANG.EN]: "Laser Treatments",
};

const UI_STRINGS = {
  [LANG.HE]: {
    title: "מחירון טיפולים",
    allCategories: "כל הקטגוריות",
    searchPlaceholder: "חיפוש לפי שם טיפול...",
    noResults: "לא נמצאו תוצאות.",
    details: "פרטים:",
    noPrices: "אין מחירים להצגה. לפרטים נוספים צרו קשר.",
    langHe: "עברית",
    langEn: "English",
    bookingCta: "לחצו כאן לקביעת תור",
  },
  [LANG.EN]: {
    title: "Treatment Price List",
    allCategories: "All categories",
    searchPlaceholder: "Search by treatment name...",
    noResults: "No results found.",
    details: "Details:",
    noPrices: "No prices to display. Contact for more info.",
    langHe: "עברית",
    langEn: "English",
    bookingCta: "Click here to book an appointment",
  },
};

/* ---------------------------------- */
/*           DATA (HEBREW)            */
/* ---------------------------------- */
const CategoryHe = {
  CONSULT: "ייעוץ",
  FACIALS: "טיפולי פנים",
  ADVANCED: "טיפולים מתקדמים / מיקרונידלינג",
  LED: "LED ותרפיה פוטודינמית",
  ADDONS: "תוספות",
  PEELS: "פילינגים מתקדמים",
  ELECTROLYSIS: "אפילציה",
  LASER_HAIR: "הסרת שיער בלייזר",
  LASER_SKIN: "לייזר לשיקום העור",
  WAX: "שעווה",
  TINT: "צביעת ריסים וגבות",
  PEDI: "פדיקור",
};

/* ---------------------------------- */
/*            DATA (ENGLISH)          */
/* ---------------------------------- */
const CategoryEn = {
  CONSULT: "Consultation",
  FACIALS: "Facials",
  ADVANCED: "Advanced / Microneedling",
  LED: "LED & Photodynamic Therapy",
  ADDONS: "Add-ons",
  PEELS: "Advanced Peels",
  ELECTROLYSIS: "Electrolysis",
  LASER_HAIR: "Laser Hair Removal",
  LASER_SKIN: "Laser Skin Rejuvenation",
  WAX: "Waxing",
  TINT: "Lash & Brow Tinting",
  PEDI: "Pedicure",
};

const pricingHe = [
  {
    id: 1,
    category: CategoryHe.CONSULT,
    title: "ייעוץ",
    description: 'אם מחליטים להמשיך מקבלים 25 ש"ח לטיפול הראשון',
    prices: [
      { name: "ייעוץ לייזר", duration: "20 דקות", amount: 50 },
      { name: "ייעוץ אפילציה", duration: "20 דקות", amount: 50 },
      { name: "ייעוץ טיפולי פנים", duration: "20 דקות", amount: 100 },
    ],
  },
  {
    id: 25,
    category: CategoryHe.ELECTROLYSIS,
    title: "אפילציה",
    description:
      "אני משתמשת במכשיר אפילציה מהמתקדמים ביותר, APILUS xCell pur, המתוכנן להעביר זרמים בטמפרטורות גבוהות ובמהירות של אלפית שנייה לזרם, ובכך למנוע נזק לעור ולהבטיח נוחות מקסימלית ללקוחה. המכשיר מציע שלוש שיטות טיפול - אלקטרוליזה, תרמוליזה ובלנד - המתאימות לכל סוג שיער, סוג עור ורמת סבילות לכאב. אני משתמשת במחטים שונות בהתאם לרגישות העור שלך. מחטי הבלט בהן אני משתמשת הן חד-פעמיות ואיכותיות.",
    prices: [
      { name: "אפילציה", duration: "עד 10 דקות", amount: 80 },
      { name: "אפילציה", duration: "עד 15 דקות", amount: 100 },
      { name: "אפילציה", duration: "עד 30 דקות", amount: 200 },
      { name: "אפילציה", duration: "עד 45 דקות", amount: 300 },
      { name: "אפילציה", duration: "עד שעה", amount: 400 },
    ],
  },
  {
    id: 3,
    category: CategoryHe.FACIALS,
    title: "טיפול אנטי-אייג'ינג החתימה של דבורה",
    description:
      "🌿 טיפול הפנים הייחודי שלי לחידוש והצערת העור 🌿\nזהו טיפול הפנים האישי והמותאם שלי – מבוסס תוצאות – שנועד לרענן, לחדש ולהחזיר חיוניות לעור עייף ומבוגר. הטיפול מתחיל בפילינג עמוק ומתקדם על בסיס תמציות עשירות בוויטמינים, וממשיך בטכניקות מותאמות אישית – בין אם בעבודת יד או באמצעות ננו-פן – לפי הצרכים המדויקים של עורך. בנוסף, אני משלבת טיפול ממוקד לאזור העיניים והצוואר. באמצעות טכנולוגיית אולטרסאונד, אני מחדירה לעור רכיבים פעילים ולחות עמוקה להזנה מבפנים. בהמשך, אני משלבת אמפולות עשירות בוויטמינים לשיפור זרימת הדם, גירוי ייצור קולגן ואלסטין, ולמראה עור חלק, גמיש וזוהר. הטיפול כולל גם עיסוי רקמות עמוק לפנים, לצוואר ולאזור המחשוף להמרצת הדם, הרפיה וחידוש עור משמעותי. לסיום, אני בוחרת עבורך מסכה מועשרת בוויטמינים – בהתאמה אישית למצב ולצרכים של עורך. \n✨ לתוצאות מיטביות מומלץ לעבור סדרת טיפולים.",
    prices: [
      { name: "טיפול אנטי־אייג'ינג חתימה", duration: "שעה וחצי", amount: 600 },
    ],
  },
  {
    id: 4,
    category: CategoryHe.FACIALS,
    title: "טיפול פנים מהיר ❤️ (לב אל לב)",
    description:
      'פעמים צריך הפסקה קצרה, צריך זמן להתנתק... הבאתי את טיפול הפנים הזה מאז ה-7 באוקטובר, וזאת כדי להציע לך זמן לנשום ולקבל טיפול. הטיפול מרגיש טוב והעור שלך מקבל את האהבה והתשומת לב שהוא צריך. אם בעלך משרת בצה"ל, אנא תדברי איתי על הנחה.',
    prices: [{ name: "טיפול פנים מהיר", duration: "30 דקות", amount: 250 }],
  },

  {
    id: 5,
    category: CategoryHe.FACIALS,
    title: "טיפול פנים קלאסי",
    description:
      "החזירי לעור פנייך את הברק הטבעי שלו עם טיפול כולל, מקיף ומותאם אישית. לאחר אבחון מעמיק של העור, תקבלי טיפול המותאם בדיוק לצרכים שלך – שילוב של פילינג עדין, אדים לפתיחת הנקבוביות, ניקוי נקודתי, החדרת לחות עמוקה ועיסוי מרגיע לפנים, צוואר ומחשוף. הטיפול מסתיים במסכת הזנה מחייה, שמותירה את העור רענן, נקי וזוהר.",
    prices: [{ name: "טיפול פנים קלאסי", duration: "שעה וחצי", amount: 500 }],
  },
  {
    id: 6,
    category: CategoryHe.FACIALS,
    title: "קסמרה",
    description:
      "טיפול פנים של חברת קסמרה (Casmara) הוא טיפול קוסמטי מקצועי המבוסס על מסיכות ייחודיות ומוצרים מתקדמים לטיפוח העור. הטיפול כולל שימוש בשתי מסיכות פעילות, כאשר השנייה מותאמת לסוג העור ולצרכי הלקוח. קסמרה מתמחה בטיפולי אנטי אייג'ינג, חידוש העור ושיקום שכבת ההגנה הטבעית של העור. הטיפולים כוללים מרכיבים טבעיים ופרוביוטיקה/פרביוטיקה לשיקום העור והשבת המראה הצעיר והבריא.",
    prices: [{ name: "טיפול Casmara", duration: "שעה", amount: 450 }],
  },
  {
    id: 7,
    category: CategoryHe.FACIALS,
    title: "טיפול פנים ללחות",
    description:
      "מיועד לעור נקי שזקוק לרענון והזנה עמוקה – הטיפול כולל ניקוי יסודי, פילינג עדין ומסכת לחות עשירה ומרגיעה. בהתאם לצורך, יתווסף גם עיסוי פנים או טכנולוגיה ייעודית להגברת זרימת הדם – לתוצאה של עור חיוני, רענן וקורן. מתאים במיוחד לעור יבש, עייף או חסר ברק.",
    prices: [{ name: "טיפול לחות", duration: "שעה", amount: 410 }],
  },
  {
    id: 8,
    category: CategoryHe.FACIALS,
    title: "ניקוי פנים בסיסי",
    description:
      "הטיפול מתאים לעור עם נקבוביות סגורות קלות וללא אקנה פעיל. כולל ניקוי עדין, פילינג קל עם אדים, ניקוי קומודונים עדין (במידת הצורך), ומסכת לחות מרגיעה. מומלץ כטיפול תחזוקה קבוע לעור חלק, רענן ומאוזן.",
    prices: [{ name: "ניקוי פנים בסיסי", duration: "שעה", amount: 410 }],
  },
  {
    id: 9,
    category: CategoryHe.FACIALS,
    title: "ניקוי עמוק או טיפול באקנה",
    description:
      "טיפול מקצועי מקיף לאיזון, חידוש ושיקום העור ברמה קלינית. הטיפול מתחיל בניקוי יסודי, ניקוז נקבוביות וחיטוי, ולאחר אני משלבת פילינגים פעילים המבוססים על חומצות מקצועיות ואנזימים צמחיים, המותאמים אישית לפי מצב העור – לתהליך עדין אך אפקטיבי של טיהור, הבהרה והתחדשות. בהמשך מתבצע פילינג יהלום מתקדם, להסרת תאים מתים, החלקת העור ושיפור המרקם והגוון. לאחר מכן, אני מבצעת טכניקת החדרה המבוססת על זרמי חשמל עדינים, המסייעת בסגירת נקבוביות ובהחדרת ניאצינמיד (ויטמין B3) לעומק העור. הטיפול כולל גם מסכת סגירה להרגעת העור והחדרת לחות, ומסתיים בשימוש בתדר גבוה לחיטוי ולהשמדת בקטריות – להשגת עור נקי, מאוזן וזוהר.",
    prices: [{ name: "ניקוי עמוק/אקנה", duration: "שעה וחצי", amount: 500 }],
  },
  {
    id: 10,
    category: CategoryHe.FACIALS,
    title: "טיפול פנים לגבר (טיפוח לבעלך!)",
    description:
      "טיפול מצוין אם אתה מחפש לתת קצת ברק לעור. נתחיל בחיתוך קל ואדים, נקיית נקבוביות עדינה לניקוי הנקבוביות, טיפול בטרפיה בתדר גבוה להשמדת חיידקים ודלקות, ונסיים במסיכת טיפול המרעננת שמשאירה את העור רענן וזוהר. *לא מתאים לעור הדורש חיתוכים מרובים.",
    prices: [{ name: "טיפול פנים לגבר", duration: "שעה", amount: 410 }],
  },
  {
    id: 11,
    category: CategoryHe.ADVANCED,
    title: "טיפול ניקוי גב",
    description:
      'הטיפול האישי שלי לבעיות עור לגב יעזור לנקות נקבוביות סתומות, דלקת ו-"בקנה". זה מיועד במיוחד לטיפוח אזורים קשים להגיע אליהם, ומתמקד בניקוי ובהפצת נקבוביות.',
    prices: [{ name: "טיפול ניקוי גב", duration: "שעה וחצי", amount: 500 }],
  },
  {
    id: 13,
    category: CategoryHe.ADVANCED,
    title: "מיקרונידלינג אנטי-אייג'ינג",
    description:
      "פציעות מבוקרות בעור. הטיפול משולב בסרומים פעילים ובאקסוזומים, המאיצים את תהליכי ההתחדשות של העור ומגבירים את ספיגת החומרים הפעילים בעד 400%. האקסוזומים מספקים גורמי גדילה רבי־עוצמה התומכים בשיקום העור ומייעלים את תהליך ההחלמה. טיפול מתקדם זה מסייע בשיפור מרקם העור, בהחלקת קמטוטים ובהשבת חיוניות וזוהר לעור, למראה צעיר, רענן ומעודן יותר. הטיפול מתחיל בהכנת העור באמצעות פילינג ייעודי ומסתיים בטיפול באור LED, התורם להאצת ההתחדשות ולתהליך הריפוי של העור.",
    prices: [
      {
        name: "מיקרונידלינג עם Exosomes / PDRN",
        duration: "שעה וחצי",
        amount: 790,
      },
    ],
  },
  {
    id: 14,
    category: CategoryHe.ADVANCED,
    title: "מיקרוניידלינג לטיפול באקנה וצלקות",
    description:
      "מיקרוניידלינג הוא טיפול בטוח ויעיל לטיפול באקנה. *איך זה עובד?* הטיפול יוצר מיקרו-תעלות בעור, כך שהרקמות מוצפות בחמצן. החמצן יעיל בנטרול חיידקי C. acnes ובכך נלחם ביעילות בזיהום בעור. *יתרונות נוספים:* • שחרור גורמי גדילה המעודדים תהליכי ריפוי טבעיים • שיפור זרימת הדם והלימפה - מספק רכיבי תזונה חיוניים לריפוי הרקמות ומסייע בסילוק רקמות צלקת. בזכות יכולת ספיגה של עד 400%, PDRN מעודד התחדשות העור, מפחית דלקות ומשפר את מרקם העור הכללי. אקסוזומים מפחיתים דלקות, מעודדים תיקון העור, צלקות, ועשויים לווסת את ייצור החלב.",
    prices: [
      { name: "מיקרונידלינג לאקנה", duration: "שעה ו-15 דקות", amount: 650 },
    ],
  },
  {
    id: 17,
    category: CategoryHe.FACIALS,
    title: "לומינ-פושיון",
    description:
      "החזרת זוהר העור ללא זמן החלמה. LuminFusion משלב טכנולוגיות מתקדמות כמו נאנו-אינפוזיה ואור אדום LED יחד עם פילינג רטינול ממוקד, להחדרת חומרים פעילים, להפחתת סימני הזדקנות העור ולהחזרת החיוניות והבהירות לעור.",
    prices: [{ name: "LuminFusion", duration: "שעה", amount: 500 }],
  },
  {
    id: 18,
    category: CategoryHe.ADDONS,
    title: "LED להוספה לטיפול הפנים",
    description:
      "טיפול ב-LED (Light Emitting Diode) הוא טיפול לא פולשני המשתמש באורכי גל ספציפיים של אור לטיפול במגוון בעיות עור ובקידום בריאות העור. הטיפול אינו כואב, אינו גורם נזק לתאים ואינו מצריך זמן החלמה.",
    prices: [{ name: "LED (תוספת)", duration: "20 דקות", amount: 150 }],
  },
  {
    id: 19,
    category: CategoryHe.ADVANCED,
    title: "תרפיה פוטוביודינמית אנטי אייג'ינג",
    description:
      "טיפול רגנרטיבי עוצמתי לחידוש העור מבוסס על רכיב ביולוגי פוטודינמי עוצמתי בשם 5-ALA (חומצה אמינולבולינית) וטכנולוגיית אור מתקדמת, לשיקום עמוק של העור מבפנים. טיפול ייחודי המאיץ תיקון נזקי שמש, סימני גיל, פיגמנטציה, רוזציאה ואקנה. טיפול לא אגרסיבי, ללא מחטים ועם תוצאות נראות לעין וללא זמן החלמה.",
    prices: [
      { name: "PDT Anti-Aging", duration: "שעה ו-15 דקות", amount: 590 },
    ],
  },
  {
    id: 20,
    category: CategoryHe.ADVANCED,
    title: "תרפיה פוטוביודינמית לאקנה וצלקות פוסט אקנה",
    description:
      "טיפול ממוקד וחדשני באקנה – עם ALA 5%. כשהעור סובל מאקנה – הגיע הזמן לגשת אליו בגישה אחרת. הטיפול עם ALA 5% משלב חומצה אמינולבולינית (ALA) עם פוטותרפיה ממוקדת, ויוצר תגובה ביוכימית עמוקה שמסייעת בניקוי בלוטות החלב, הפחתת דלקת, ויסות ייצור שומן ושיפור כללי של מרקם העור. \n✨ מתאים במיוחד לאקנה דלקתי, עור שמן ובעייתי. \n✨ עובד לעומק – בלי לפצוע, בלי לקלף. \n✨ מבוסס מדע – טכנולוגיה רפואית שפוגשת טיפול אסתטי מדויק.",
    prices: [{ name: "PDT לאקנה", duration: "שעה", amount: 490 }],
  },
  {
    id: 21,
    category: CategoryHe.ADDONS,
    title: "עיסוי פנים באבנים חמות",
    description:
      "🌿🪨 טיפול מפנק ומשחרר הכולל שימוש באבנים חמות וקטנות המותאמות במיוחד לעור הפנים. החום המרגיע 🌸 בשילוב טכניקות עיסוי עדינות 🤲 מסייעים לשחרור מתחים בשרירי הפנים, לשיפור זרימת הדם ולהענקת תחושת רוגע עמוקה וזוהר טבעי ✨.",
    prices: [
      { name: "עיסוי פנים באבנים חמות", duration: "15 דקות", amount: 90 },
    ],
  },
  {
    id: 22,
    category: CategoryHe.ADDONS,
    title: "תוספת אולטרסאונד ו-EXO-GEL",
    description:
      "הטיפול באולטרסאונד מאפשר לי לעבוד לעומק מבלי לפגוע בעור. הוא מסייע במתיחה ובמיצוק של עור הפנים והצוואר, בצמצום קמטים, ובהמרצת ייצור הקולגן. בנוסף, הוא משפר את ספיגת החומרים הפעילים ומגביר את זרימת החמצן לתאים – מה שתורם לעור חיוני, חלק וזוהר יותר.",
    prices: [{ name: "EXO-GEL + אולטרסאונד", duration: "15 דקות", amount: 100 }],
  },
  {
    id: 23,
    category: CategoryHe.ADDONS,
    title: "הסרת נגעים",
    description:
      "הסרת נגעים, יבלות, מיליה, סרחי עור, ורידי עכביש ונקודות אדומות (Cherry Angiomas).",
    prices: [{ name: "Blemish removal", duration: "10 דקות", amount: 100 }],
  },

  {
    id: 26,
    category: CategoryHe.LASER_HAIR,
    title: "הסרת שיער בלייזר",
    description: "שלמו מראש עבור 5 וקבלו חצי מחיר על החמישי.",
    prices: [
      { name: "ידיים מלאות", duration: "20 דקות", amount: 250 },
      { name: "יד תחתונה", duration: "15 דקות", amount: 180 },
      { name: "בית שחי", duration: "10 דקות", amount: 70 },
      { name: "רגליים מלא", duration: "40 דקות", amount: 400 },
      { name: "חצי רגל עליונה", duration: "20 דקות", amount: 300 },
      { name: "חצי רגל תחתונה", duration: "20 דקות", amount: 220 },
      { name: "רגל מלא ופס ביקיני", duration: "45 דקות", amount: 470 },
      { name: "פס ביקיני", duration: "15 דקות", amount: 80 },
      { name: "ישבן", duration: "20 דקות", amount: 100 },
      { name: "פס בטן", duration: "10 דקות", amount: 50 },
      { name: "ביקיני עמוק", duration: "15 דקות", amount: 100 },
      { name: "מפשעות מלא", duration: "30 דקות", amount: 150 },
      { name: "גב מלא", duration: "30 דקות", amount: 350 },
      { name: "גב עליון/תחתון", duration: "20 דקות", amount: 200 },
      { name: "פנים מלא", duration: "30 דקות", amount: 150 },
      { name: "פאות", duration: "15 דקות", amount: 80 },
      { name: "שפם", duration: "10 דקות", amount: 50 },
      { name: "סנטר", duration: "20 דקות", amount: 50 },
      { name: "שפם וסנטר", duration: "20 דקות", amount: 90 },
      { name: "כתפיים", duration: "15 דקות", amount: 70 },
      { name: "שפם, שפה תחתונה וסנטר", duration: "20 דקות", amount: 100 },
      { name: "קו חזה", duration: "20 דקות", amount: 70 },
      { name: "בין הגבות", duration: "5 דקות", amount: 40 },
      { name: "בטן", duration: "10 דקות", amount: 120 },
      { name: "עורף", duration: "20 דקות", amount: 100 },
      { name: "צוואר", duration: "20 דקות", amount: 80 },
      { name: "סביב השיער", duration: "30 דקות", amount: 90 },
      { name: "גוף מלא", duration: "שעה", amount: 720 },
    ],
  },
  {
    id: 27,
    category: CategoryHe.LASER_SKIN,
    title: "טיפולי לייזר לעור",
    description:
      "טיפולים מתקדמים בלייזר להסרת כתמים, ריפוי צלקות אקנה וחידוש העור.",
    prices: [
      { name: "התחדשות עור בלייזר", duration: "שעה", amount: 450 },
      { name: "טיפול באקנה באמצעות לייזר", duration: "20 דקות", amount: 250 },
    ],
  },
  {
    id: 28,
    category: CategoryHe.WAX,
    title: "טיפולי שעווה",
    description: "טיפוח והסרת שיער בשעווה לכל חלקי הגוף.",
    prices: [
      { name: "גבות", duration: "15 דקות", amount: 55 },
      { name: "שפם וגבות", duration: "20 דקות", amount: 65 },
      { name: "שפם", duration: "10 דקות", amount: 30 },
      { name: "סנטר", duration: "5 דקות", amount: 50 },
      { name: "שפם וסנטר", duration: "10 דקות", amount: 60 },
      { name: "אף", duration: "5 דקות", amount: 20 },
      { name: "פאות", duration: "10 דקות", amount: 60 },
      { name: "צוואר", duration: "10 דקות", amount: 75 },
      { name: "בית השחי", duration: "15 דקות", amount: 65 },
      { name: "קו בטן", duration: "10 דקות", amount: 40 },
      { name: "בטן", duration: "20 דקות", amount: 75 },
      { name: "קו ביקיני", duration: "15 דקות", amount: 80 },
      { name: "ביקיני עמוק", duration: "20 דקות", amount: 100 },
      { name: "מפשעות", duration: "30 דקות", amount: 250 },
      { name: "חצי רגל תחתונה", duration: "20 דקות", amount: 75 },
      { name: "רגליים מלא", duration: "45 דקות", amount: 150 },
      { name: "רגליים מלאות וביקיני", duration: "45 דקות", amount: 220 },
      { name: "ידיים מלא", duration: "20 דקות", amount: 100 },
      { name: "חצי יד תחתונה", duration: "15 דקות", amount: 70 },
      { name: "גב", duration: "20 דקות", amount: 130 },
    ],
  },
  {
    id: 29,
    category: CategoryHe.TINT,
    title: "צביעת ריסים וגבות",
    description: "הדגשה והגדרה של הריסים והגבות עם צביעה מקצועית.",
    prices: [
      { name: "צביעת גבות", duration: "15 דקות", amount: 50 },
      { name: "צביעת ריסים", duration: "20 דקות", amount: 70 },
    ],
  },
  {
    id: 30,
    category: CategoryHe.ADVANCED,
    title: "גרין פיל (Green Peel)",
    description: "(פילינג) צמחי וטבעי לחלוטין (100%) שפותח מבחינה רפואית. הטיפול מחדש ומשקם את עור הפנים ללא שימוש בחומצות סינתטיות, תוספים כימיים או מכשירים פולשניים. הטיפול פותח על ידי רופאת העור הגרמנייה ד\"ר כריסטיאן שראמק-דרוזיו, והבסיס שלו הוא תערובת ייחודית של שמונה צמחי מרפא נבחרים, אנזימים, ויטמינים ומינרלים המעוסים באופן ידני אל תוך העור. תהליך זה ממריץ את זרימת הדם, משפר את אספקת החמצן ומאיץ את תחלופת התאים הטבעית.",
    prices: [{ name: "גרין פיל", duration: "שעה וחצי", amount: 700 }],
  },
];
const pricingEn = [
  {
    id: 1,
    category: CategoryEn.CONSULT,
    title: "Consultation",
    description:
      "If you decide to go forward with the treatment you will get 25 shekels towards your first treatment.",
    prices: [
      { name: "Laser Consultation", duration: "20 min", amount: 50 },
      { name: "Electrolysis Consultation", duration: "20 min", amount: 50 },
      { name: "Skincare consultation", duration: "20 min", amount: 100 },
    ],
  },

  {
    id: 25,
    category: CategoryEn.ELECTROLYSIS,
    title: "Electrolysis",
    description:
      "I use a top-of-the-range electrolysis machine, the APILUS xCell pur, which is designed to deliver currents at high temperatures with a speed of a 1000th of a second per current, avoiding skin damage and assuring client comfort. It offers three modalities - electrolysis, thermoysis and blend - for ANY hair or skin type and pain tolerance. I use different probes according to your skin's sensitivity. The ballet probes I use are disposable and of high quality.",
    prices: [
      { name: "Electrolysis", duration: "up to 10 min", amount: 80 },
      { name: "Electrolysis ", duration: "15 min", amount: 100 },
      { name: "Electrolysis ", duration: "30 min", amount: 200 },
      { name: "Electrolysis ", duration: "45 min", amount: 300 },
      { name: "Electrolysis ", duration: "60 min", amount: 400 },
    ],
  },
  {
    id: 3,
    category: CategoryEn.FACIALS,
    title:
      "Devorah's signature anti-aging facial, including neck and decollete",
    description:
      "🌿 My Signature Rejuvenating Facial 🌿\nThis is my personal, results-driven facial treatment designed to renew and refresh tired, aging skin. It begins with an advanced deep peel using vitamin-rich blends, followed by customized techniques—whether with my hands or a nano pen—tailored to your skin’s specific needs. I also include targeted treatment for the eye and neck areas. Using ultrasound technology, I infuse active nutrients and deep hydration into the skin to nourish from within. Ampoules rich in vitamins are incorporated to boost circulation, stimulate collagen and elastin production, and leave your skin looking smooth, supple, and radiant. Using a deep tissue massage including face, neck and decollate I'll end off with a choice of many vitamin infused masks tailored for your needs. \n✨ Best results are achieved with a series of treatments.",
    prices: [
      { name: "Signature Anti-Aging", duration: "1.5 hours", amount: 600 },
    ],
  },
  {
    id: 4,
    category: CategoryEn.FACIALS,
    title: "A heart to heart (quick) facial ❤️",
    description:
      "Need a short break, need some time to switch off, a time to breathe and be cared for. It's a feel-good treatment and your skin gets some much needed love and attention.",
    prices: [{ name: "Heart to Heart", duration: "30 min", amount: 250 }],
  },

  {
    id: 5,
    category: CategoryEn.FACIALS,
    title: "Classic Facial",
    description:
      "Restore your skin's natural brilliance with this full, all-in-one treatment. After analysing your skin, you’ll receive a tailored treatment for your needs, with a combination of exfoliation, steam, blemish extractions, deep moisture infusion, as well as relaxing face, neck and décolleté massage. We’ll then conclude with a rejuvenating treatment mask to leave your skin clean, fresh and hydrated.",
    prices: [{ name: "Classic Facial", duration: "1h 15m", amount: 500 }],
  },
  {
    id: 6,
    category: CategoryEn.FACIALS,
    title: "Casmara Facial",
    description:
      "Casmara facial treatments are professional skincare treatments that utilize high-quality, marine-based products, particularly algae extracts, to address various skin concerns. These treatments are known for their hydrating, firming, and revitalizing effects, with immediately visible results.",
    prices: [{ name: "Casmara Facial", duration: "1 hour", amount: 450 }],
  },
  {
    id: 7,
    category: CategoryEn.FACIALS,
    title: "Hydrating Facial",
    description:
      "Designed for skin that’s clear but craves hydration, this facial delivers a cleanse followed by a gentle exfoliation and a replenishing hydrating mask. Might include facial massage or technology to boost circulation and leave the skin plump, dewy, and radiant. Perfect for dry, tired, or dull-looking skin.",
    prices: [{ name: "Hydration Facial", duration: "1 hour", amount: 410 }],
  },
  {
    id: 8,
    category: CategoryEn.FACIALS,
    title: "Basic Facial Cleanse",
    description:
      "This facial is perfect for clients with minimal congestion and no active acne. Includes a cleanse, light exfoliation with steam and gentle extractions (if needed) and a hydrating mask. Ideal for maintenance and leaving the skin smooth, and refreshed.",
    prices: [{ name: "Basic Cleanse", duration: "1 hour", amount: 410 }],
  },
  {
    id: 9,
    category: CategoryEn.FACIALS,
    title: "Specialised deep cleanse / acne facial",
    description:
      "After cleansing and assessing your skin, I'll prepare your skin with a specialised peeling, steam and diamond peeling with suction, this resurfaces your skin, softens impurities for comfortable, effective extraction. Advanced electrical techniques then close pores while infusing niacinamide (vitamin B3) to strengthen your skin barrier. A calming rehydration mask follows, and conclude with high-frequency treatment to eliminate bacteria and leave your skin genuinely fresh, balanced, with an even texture.",
    prices: [
      { name: "Deep Cleanse / Acne", duration: "1.5 hours", amount: 500 },
    ],
  },
  {
    id: 10,
    category: CategoryEn.FACIALS,
    title: "The gentleman's facial (treat your husband!)",
    description:
      "A great treatment if you’re looking for a skin boost. We’ll begin with light exfoliation and steam, gentle extractions to cleanse your pores, high-frequency therapy to eliminate bacteria & inflammation, and conclude with a rejuvenating mask to leave your skin fresh and glowing. *Not suitable for skin that requires extensive extractions.",
    prices: [{ name: "Men’s Facial", duration: "1 hour", amount: 410 }],
  },
  {
    id: 11,
    category: CategoryEn.ADVANCED,
    title: "Back cleaning (b'acne treatment)",
    description:
      "My customized back treatment helps clear clogged pores, inflammation and “bacne”. It’s designed specially to treat those hard-to-reach areas, focusing on cleansing and extractions.",
    prices: [{ name: "Back Cleanse", duration: "1.5 hours", amount: 500 }],
  },
  {
    id: 13,
    category: CategoryEn.ADVANCED,
    title: "Microneedling for anti-aging",
    description:
      "Microneedling is a minimally invasive treatment that stimulates collagen production by creating tiny, controlled micro-injuries in the skin, enhanced serums or Exosomes, accelerates skin regeneration by maximizing absorption—up to 400%. Exosomes deliver potent growth factors that optimize skin recovery. This advanced treatment stimulates collagen production, smooths texture, and visibly restores skin vitality, providing a more rejuvenated and refined appearance. The skin is prepared with a specialised peeling treatment and ends off with LED light therapy further promoting regeneration and skin recovery.",
    prices: [
      {
        name: "Microneedling with Exosomes / PDRN",
        duration: "1.5 hours",
        amount: 790,
      },
    ],
  },
  {
    id: 14,
    category: CategoryEn.ADVANCED,
    title: "Microneedling for acne and post acne scaring",
    description:
      "Microneedling is a safe and effective treatment for acne.\n-It creates micro-channels within the skin, this floods the tissues with oxygen. The oxygen is effective at neutralising C. acnes Bacteria, hence fighting the infection in the skin. \n-The release of Growth Factors promote healing.\n-Increased blood and lymph circulation – delivers nutrients to facilitate healing of tissues and aids in waste removal of scar tissue\nWith up to 400% absorption, PDRN promotes skin regeneration, reducing inflammation,  improving overall skin texture. \nExosomes, reduce inflammation, promoting skin repair, and potentially regulating sebum production.",
    prices: [{ name: "Acne Microneedling", duration: "1h 15m", amount: 650 }],
  },
  {
    id: 17,
    category: CategoryEn.FACIALS,
    title: "LuminFusion",
    description:
      "Reviving skin luminosity \nLuminFusion supercharges skin smoothness and luminosity with no downtime. High-tech modalities including Nanoinfusion and LED red light combined with a targeted Retinol Peel, infuse actives to reduce the appearance of skin aging and restore skin vibrancy.",
    prices: [{ name: "LuminFusion", duration: "1 hour", amount: 500 }],
  },
  {
    id: 18,
    category: CategoryEn.ADDONS,
    title: "LED with your facial treatment",
    description:
      "Advanced light wavelengths penetrate the skin at different depths to target specific concerns:\n🔵 Blue light reduces acne-causing bacteria and balances oil production.\n🔴 Red light stimulates collagen, improves circulation, and supports skin repair.\n🟣 Purple light combines red and blue benefits to calm inflammation and promote overall skin rejuvenation.",
    prices: [{ name: "LED Add-on", duration: "30 min", amount: 150 }],
  },
  {
    id: 19,
    category: CategoryEn.ADVANCED,
    title: "Advanced Renewal Peel for anti-aging",
    description:
      "A powerful resurfacing treatment combining a 5% ALA (Aminolevulinic Acid) photodynamic serum with advanced light therapy to deeply rejuvenate the skin from within. This high-performance peel targets uneven tone, sun damage, enlarged pores, and signs of aging by stimulating cellular turnover and refining skin texture. The photodynamic activation enhances treatment penetration and boosts collagen production, leaving the skin noticeably smoother, clearer, and more radiant. Suitable for clients seeking clinical-grade skin correction with minimal downtime.",
    prices: [{ name: "PDT Anti-Aging", duration: "1h 15m", amount: 590 }],
  },
  {
    id: 20,
    category: CategoryEn.ADVANCED,
    title: "Advanced Renewal Peel for skin break outs and acne",
    description:
      "A results-driven treatment designed for acne-prone skin, targeting enlarged pores and refining skin texture. This advanced peel blends azelaic, mandelic, and salicylic acids to exfoliate, reduce breakouts, and calm inflammation. Finished with an ALA serum and blue LED light therapy to target acne-causing bacteria, support barrier repair, and promote deep skin healing from within. Ideal for clients seeking clinical-grade correction with minimal downtime.",
    prices: [{ name: "PDT Acne", duration: "1 hour", amount: 490 }],
  },
  {
    id: 21,
    category: CategoryEn.ADDONS,
    title: "Hot stone facial massage",
    description:
      "Extra-relaxing add-on to release tension and boost circulation.",
    prices: [
      { name: "Hot Stone Facial Massage", duration: "15 min", amount: 90 },
    ],
  },
  {
    id: 22,
    category: CategoryEn.ADDONS,
    title: "Extra 15 mins anti-aging ultrasound with specialized gel",
    description:
      "Increase blood flow, collagen and elastin to the skin with the help of exo-gel, the latest generation in anti-aging formulas.",
    prices: [{ name: "EXO-GEL + Ultrasound", duration: "15 min", amount: 100 }],
  },
  {
    id: 23,
    category: CategoryEn.ADDONS,
    title: "Blemish removal",
    description:
      "For removal of warts, milia, skin tags, cherry angiomas and thread veins.",
    prices: [{ name: "Blemish Removal", duration: "10 min", amount: 100 }],
  },

  {
    id: 26,
    category: CategoryEn.LASER_HAIR,
    title: "Laser Hair Removal",
    description: "Prepay for 5, get 50% off the fifth.",
    prices: [
      { name: "Full Arms", duration: "20 min", amount: 250 },
      { name: "Lower half arm", duration: "15 min", amount: 180 },
      { name: "Underarms", duration: "10 min", amount: 70 },
      { name: "Full Legs", duration: "40 min", amount: 400 },
      { name: "Upper half leg", duration: "20 min", amount: 300 },
      { name: "Lower half leg", duration: "20 min", amount: 220 },
      { name: "Full leg and bikini strip", duration: "45 min", amount: 470 },
      { name: "Bikini", duration: "15 min", amount: 80 },
      { name: "Brazilian", duration: "30 min", amount: 150 },
      { name: "Stomach line", duration: "10 min", amount: 50 },
      { name: "Deep bikini", duration: "15 min", amount: 100 },
      { name: "Full back", duration: "30 min", amount: 350 },
      { name: "Half Back (upper/lower back)", duration: "20 min", amount: 200 },
      { name: "Full face", duration: "30 min", amount: 150 },
      { name: "Sideburns", duration: "15 min", amount: 80 },
      { name: "Upper Lip", duration: "10 mins", amount: 50 },
      { name: "Chin", duration: "20 min", amount: 50 },
      { name: "Upper lip and chin", duration: "20 min", amount: 90 },
      { name: "Shoulders", duration: "15 min", amount: 70 },
      {
        name: "Upper lip, chin and lower lip",
        duration: "20 min",
        amount: 100,
      },
      { name: "Chest line", duration: "20 min", amount: 70 },
      { name: "Between eyebrows", duration: "5 min", amount: 40 },
      { name: "Stomach", duration: "10 min", amount: 120 },
      { name: "Back of neck", duration: "20 min", amount: 100 },
      { name: "Neck front", duration: "20 min", amount: 80 },
      { name: "Hair line", duration: "30 min", amount: 90 },
      { name: "Behind", duration: "20 min", amount: 100 },
      { name: "Full Body", duration: "1 hour", amount: 720 },
    ],
  },
  {
    id: 27,
    category: CategoryEn.LASER_SKIN,
    title: "Laser Skin Treatments",
    description:
      "Advanced laser treatments to target pigmentation, acne, and signs of aging.",
    prices: [
      { name: "Laser skin rejuvenation", duration: "1 hour", amount: 450 },
      { name: "Laser acne treatment", duration: "20 min", amount: 250 },
    ],
  },
  {
    id: 28,
    category: CategoryEn.WAX,
    title: "Wax treatment",
    description: "Professional waxing for the whole body.",
    prices: [
      { name: "Eyebrows", duration: "15 min", amount: 55 },
      { name: "Lip and Brow", duration: "20 min", amount: 65 },
      { name: "Lip", duration: "10 min", amount: 30 },
      { name: "Chin", duration: "5 min", amount: 50 },
      { name: "Lip & chin", duration: "10 min", amount: 60 },
      { name: "Nose", duration: "5 min", amount: 20 },
      { name: "Sideburns", duration: "10 min", amount: 60 },
      { name: "Neck", duration: "10 min", amount: 75 },
      { name: "Underarm", duration: "15 min", amount: 65 },
      { name: "Stomach line", duration: "10 min", amount: 40 },
      { name: "Stomach", duration: "20 min", amount: 75 },
      { name: "Bikini line", duration: "15 min", amount: 80 },
      { name: "Deep bikini", duration: "20 min", amount: 100 },
      { name: "Brazilian", duration: "30 min", amount: 250 },
      { name: "Half leg", duration: "20 min", amount: 75 },
      { name: "Full leg", duration: "45 min", amount: 150 },
      { name: "Full leg and bikini", duration: "45 min", amount: 220 },
      { name: "Full arm", duration: "20 min", amount: 100 },
      { name: "Lower half arm", duration: "15 min", amount: 70 },
      { name: "Full back", duration: "20 min", amount: 130 },
    ],
  },
  {
    id: 29,
    category: CategoryEn.TINT,
    title: "Eyelash and eyebrow tinting",
    description: "Enhance and define lashes & brows with professional tinting.",
    prices: [
      { name: "eyebrow tint", duration: "15 min", amount: 50 },
      { name: "Eyelash tint", duration: "20 min", amount: 70 },
    ],
  },
  {
    id: 30,
    category: CategoryEn.ADVANCED,
    title: "Dr. Schrammek Green Peel",
    description: "The Dr. Schrammek Green Peel is a natural, plant-based herbal resurfacing treatment designed to stimulate skin regeneration from within. Developed by dermatologists, it uses a specialized blend of herbs, enzymes, vitamins, and minerals to improve texture, boost collagen, and treat acne without harsh chemicals.",
    prices: [{ name: "Green Peel", duration: "1.5 hours", amount: 700 }],
  },
];

/* 
  Icons:
  import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
  import { FiSearch, FiX } from "react-icons/fi";
*/

/* ---------------------------------- */
/*             HELPERS                */
/* ---------------------------------- */
function formatAmount(n, lang) {
  return n.toLocaleString(lang === LANG.HE ? "he-IL" : "en-US", {
    style: "currency",
    currency: "ILS",
  });
}

const PriceBadge = ({ children }) =>
  children ? (
    <span className="inline-block px-2.5 py-0.5 rounded-full bg-purple-50 text-purple-600 text-xs font-medium tracking-wide ml-2">
      {children}
    </span>
  ) : null;

const Amount = ({ children }) => (
  <span className="font-medium text-gray-900 text-lg">{children}</span>
);

const PricesList = ({ prices = [], lang }) => {
  const t = UI_STRINGS[lang];
  if (!prices.length) {
    return (
      <div className="mt-6 rounded-lg border border-gray-100 bg-gray-50 p-4 text-gray-500 text-sm text-center">
        {t.noPrices}
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-lg border border-gray-100 bg-gray-50/50 divide-y divide-gray-100 overflow-hidden">
      {prices.map((p, idx) => (
        <div key={idx} className="p-4 transition-colors hover:bg-white">
          <div className="flex flex-col gap-1">
            <div className="flex justify-between items-start gap-4">
              <div className="font-medium text-gray-900 text-lg leading-snug">
                {p.name}
              </div>
              <Amount>{formatAmount(p.amount, lang)}</Amount>
            </div>
            {p.duration && (
              <div className="flex items-center gap-2 mt-1">
                <PriceBadge>{p.duration}</PriceBadge>
              </div>
            )}
          </div>
          {p.note && (
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              {p.note}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

const LanguageBar = ({ lang, setLang }) => {
  return (
    <div className="w-full">
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-xs font-semibold tracking-widest text-gray-400 uppercase hidden sm:block">
          Select Language
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={() => {
              setLang(LANG.HE);
              track("lang", { lang: LANG.HE });
            }}
            className={`px-4 py-1.5 rounded-full text-[10px] font-bold tracking-wide transition-all duration-300 uppercase
              ${
                lang === LANG.HE
                  ? "bg-purple-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
          >
            עברית
          </button>
          <button
            onClick={() => {
              setLang(LANG.EN);
              track("lang", { lang: LANG.EN });
            }}
            className={`px-4 py-1.5 rounded-full text-[10px] font-bold tracking-wide transition-all duration-300 uppercase
              ${
                lang === LANG.EN
                  ? "bg-purple-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
          >
            English
          </button>
        </div>
      </div>
    </div>
  );
};

const ServiceItem = ({ service, isOpen, onToggle, lang }) => {
  const t = UI_STRINGS[lang];

  return (
    <Accordion
      dir="ltr"
      className={`group mb-4 relative overflow-hidden rounded-xl bg-white transition-all duration-500 ease-out
      ${
        isOpen
          ? "shadow-xl ring-1 ring-black/5"
          : "shadow-sm hover:shadow-md border border-gray-100"
      }`}
      transition={{
        duration: "300ms",
        timingFunction: "cubic-bezier(0, 0, 0.2, 1)",
      }}
    >
      {/* 
          PURPLE SIDE LINE: 
          Fixed width (w-1) so it doesn't change size.
          Always visible at full opacity.
      */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-600" />

      <AccordionItem>
        {(() => {
          const isRTL = lang === LANG.HE;
          const ArrowIcon = isRTL ? IoIosArrowBack : IoIosArrowForward;

          return (
            <>
              <AccordionHeader
                className={`flex items-center justify-between p-6 w-full cursor-pointer select-none ${
                  isRTL ? "flex-row" : "flex-row-reverse"
                }`}
                onClick={onToggle}
              >
                <div
                  className={`transition-all duration-300 ${isOpen ? "text-purple-600 rotate-90" : "text-purple-500"}`}
                >
                  <ArrowIcon size={20} />
                </div>

                <h3
                  className={`text-xl font-medium tracking-tight transition-colors duration-300 text-gray-900
                   ${isRTL ? "text-right" : "text-left"}`}
                  dir={isRTL ? "rtl" : "ltr"}
                >
                  {service.title}
                </h3>
              </AccordionHeader>

              <AccordionBody className="px-6">
                <div className="mx-auto pb-6" dir={isRTL ? "rtl" : "ltr"}>
                  <div className="pt-2">
                    {/* Booking Button */}
                    <div className="mb-6">
                      <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track("book", { treatment: service.title, lang })}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold bg-purple-600 text-white hover:bg-purple-700 transition-all shadow-sm active:scale-95"
                      >
                        <span>{t.bookingCta}</span>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          className={isRTL ? "rotate-180" : ""}
                        >
                          <path
                            d="M5 12H19M19 12L12 5M19 12L12 19"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </a>
                    </div>

                    {(service.description || service.bullets) && (
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 mt-2">
                        {t.details}
                      </p>
                    )}

                    {service.description && (
                      <p className="text-gray-600 leading-relaxed">
                        {service.description}
                      </p>
                    )}

                    {service.bullets && (
                      <ul className="list-disc list-inside text-gray-600 leading-relaxed mt-2 space-y-1">
                        {service.bullets.map((b, i) => (
                          <li key={i}>{b}</li>
                        ))}
                      </ul>
                    )}

                    {service.additionalInfo && (
                      <p className="font-medium text-base text-gray-700 bg-gray-50 p-3 rounded-lg my-3 border-l-2 border-purple-600">
                        {service.additionalInfo}
                      </p>
                    )}

                    {service.externalLink && (
                      <a
                        href={service.externalLink.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-bold text-purple-600 hover:text-purple-800 underline decoration-2 underline-offset-4 block mb-4"
                      >
                        {service.externalLink.text}
                      </a>
                    )}

                    <PricesList prices={service.prices} lang={lang} />
                  </div>
                </div>
              </AccordionBody>
            </>
          );
        })()}
      </AccordionItem>
    </Accordion>
  );
};

/* ---------------------------------- */
/*            MAIN COMPONENT          */
/* ---------------------------------- */
const Accordions = () => {
  const [openMap, setOpenMap] = useState({});
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [lang, setLang] = useState(LANG.HE);

  const data = lang === LANG.HE ? pricingHe : pricingEn;
  const t = UI_STRINGS[lang];

  const getDisplayCategory = (cat) => {
    const isLaser =
      cat ===
        (lang === LANG.HE ? CategoryHe.LASER_HAIR : CategoryEn.LASER_HAIR) ||
      cat ===
        (lang === LANG.HE ? CategoryHe.LASER_SKIN : CategoryEn.LASER_SKIN);
    if (isLaser) return MERGED_LASER_CATEGORY[lang];
    return cat;
  };

  const allCategories = useMemo(() => {
    const set = new Set();
    data.forEach((s) => set.add(getDisplayCategory(s.category)));
    return ["all", ...Array.from(set)];
  }, [data, lang]);

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase();
    return data.filter((service) => {
      const serviceCat = getDisplayCategory(service.category);
      const matchCategory =
        categoryFilter === "all" || categoryFilter === serviceCat;
      if (!matchCategory) return false;
      if (!term) return true;
      return (
        service.title?.toLowerCase().includes(term) ||
        service.description?.toLowerCase().includes(term) ||
        (service.prices || []).some((p) => p.name?.toLowerCase().includes(term))
      );
    });
  }, [data, search, categoryFilter, lang]);

  const grouped = useMemo(() => {
    return filteredData.reduce((acc, s) => {
      const cat = getDisplayCategory(s.category);
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(s);
      return acc;
    }, {});
  }, [filteredData, lang]);

  const toggle = (service) => {
    if (!openMap[service.id]) track("open_treatment", { treatment: service.title, lang });
    setOpenMap((p) => ({ ...p, [service.id]: !p[service.id] }));
  };
  const clearSearch = () => setSearch("");

  return (
    <div className="py-6 pb-44">
      <LanguageBar lang={lang} setLang={setLang} />

      <div className="max-w-2xl mx-auto px-4 mt-6 mb-4">
        <div className={lang === LANG.HE ? "text-right" : "text-left"}>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            {t.title}
          </h1>
        </div>
      </div>

      <div className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-xl border-b border-gray-100 shadow-sm transition-all duration-300">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="relative group">
            <div
              className={`absolute top-1/2 -translate-y-1/2 transition-colors ${lang === LANG.HE ? "right-4" : "left-4"} text-gray-400 group-focus-within:text-purple-600`}
            >
              <FiSearch size={18} />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => {
                if (search.trim()) track("search", { term: search.trim(), lang });
              }}
              placeholder={t.searchPlaceholder}
              className={`w-full py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-purple-600 focus:ring-1 focus:ring-purple-600 transition-all
                ${lang === LANG.HE ? "pr-10 pl-4" : "pl-10 pr-4"}`}
              dir={lang === LANG.HE ? "rtl" : "ltr"}
            />
            {search && (
              <button
                onClick={clearSearch}
                className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 p-1 ${lang === LANG.HE ? "left-2" : "right-2"}`}
              >
                <FiX size={16} />
              </button>
            )}
          </div>

          <div
            className={`flex gap-2 overflow-x-auto pt-3 pb-1 no-scrollbar ${lang === LANG.HE ? "flex-row-reverse" : ""}`}
          >
            {allCategories.map((c) => (
              <button
                key={c}
                onClick={() => {
                  setCategoryFilter(c);
                  track("category", { category: c === "all" ? "all" : c, lang });
                }}
                className={`whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200 border
                  ${
                    categoryFilter === c
                      ? "bg-purple-600 text-white border-purple-600 shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
              >
                {c === "all" ? t.allCategories : c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[450px] md:max-w-[1000px] mx-auto mt-6 px-4">
        {Object.entries(grouped).map(([category, items]) => (
          <section key={category} className="mb-6">
            <h2
              className={`text-xs font-bold tracking-widest uppercase mt-10 mb-4 flex items-center gap-4 text-gray-400 ${
                lang === LANG.HE ? "flex-row" : "flex-row-reverse justify-end"
              }`}
              dir={lang === LANG.HE ? "rtl" : "ltr"}
            >
              <span className="whitespace-nowrap">{category}</span>
              <div className="h-px bg-gray-200 w-full"></div>
            </h2>

            {items.map((service) => (
              <ServiceItem
                key={service.id}
                service={service}
                isOpen={!!openMap[service.id]}
                onToggle={() => toggle(service)}
                lang={lang}
              />
            ))}
          </section>
        ))}

        {/* Global Floating Booking CTA */}
        <div className="fixed bottom-6 left-0 right-0 z-40 flex justify-center pointer-events-none">
          <div className="pointer-events-auto">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("book", { treatment: "floating button", lang })}
              className="px-8 py-3 rounded-full font-bold text-base bg-purple-600 text-white hover:bg-purple-700 hover:scale-105 active:scale-95 transition-all duration-300 shadow-lg flex items-center gap-2"
              dir={lang === LANG.HE ? "rtl" : "ltr"}
            >
              <span>{t.bookingCta}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className={lang === LANG.HE ? "rotate-180" : ""}
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accordions;
